/**
 * algolia-insights-filter.js
 *
 * Network-level filter for Algolia Insights events.
 *
 * This module patches fetch, navigator.sendBeacon, and XMLHttpRequest so that
 * outbound requests to insights.algolia.io are filtered before they leave the
 * browser. It runs on EVERY page (via globalBundle) — including international
 * markets where algolia-header.js is not loaded — which is necessary because
 * GTM's insights-gtm plugin has its own Algolia client instance and fires
 * events independently of any aa.sendEvents patch in algolia-header.js.
 *
 * Filtering rules for tracked conversion events (Add-to-Cart / Purchase):
 *  1. Block if window.algoliaData.shouldAllowAlgoliaEvents is not true.
 *     The CMS sets this to true only for supported NA markets (USD/CAD).
 *     On all other markets (AU, EU, etc.) it is false or absent.
 *  2. Block if queryID or index is missing (the event would get a 422 anyway).
 */
/* global Response, XMLHttpRequest */
(function () {
    var INSIGHTS_HOST = 'insights.algolia.io';

    // Returns true if the CMS has flagged this market as supported for Algolia Insights.
    // window.algoliaData.shouldAllowAlgoliaEvents is set to true only for NA markets.
    function isAlgoliaEventsAllowed() {
        return window.algoliaData && window.algoliaData.shouldAllowAlgoliaEvents === true;
    }

    // Returns true = event is valid and may be sent.
    // Returns false = event should be dropped.
    function isValidTrackedConversion(event) {
        if(!event || typeof event !== 'object') return true;
        if(event.eventType !== 'conversion') return true;

        var subtype = (event.eventSubtype || '').trim().toLowerCase();
        var name = (event.eventName || '').trim().toLowerCase();
        var isTracked = subtype === 'addtocart' || subtype === 'purchase' ||
            name === 'add-to-cart' || name === 'algolia-purchase';

        if(!isTracked) return true; // not a tracked conversion type — pass through

        // 1. Block if this market is not flagged as supported
        if(!isAlgoliaEventsAllowed()) {
            console.log('[Algolia] Insights filter: blocked event — market not supported (shouldAllowAlgoliaEvents is not true):', event);
            return false;
        }

        // 2. Block if queryID or index is missing (would result in a 422)
        var hasQueryId = typeof event.queryID === 'string' && event.queryID.trim() !== '';
        var hasIndex = typeof event.index === 'string' && event.index.trim() !== '';
        if(!hasQueryId || !hasIndex) {
            console.log('[Algolia] Insights filter: blocked tracked conversion missing queryID/index:', event);
            return false;
        }

        return true;
    }

    function filterInsightsBody(bodyText) {
        var parsed;
        try { parsed = JSON.parse(bodyText); } catch (e) { return bodyText; }
        if(!parsed || !Array.isArray(parsed.events)) return bodyText;

        var filtered = parsed.events.filter(isValidTrackedConversion);
        if(filtered.length === parsed.events.length) return bodyText; // nothing removed
        if(!filtered.length) return null; // all events blocked — signal to suppress request
        return JSON.stringify({ events: filtered });
    }

    // --- Patch fetch ---
    if(typeof window.fetch === 'function') {
        var _originalFetch = window.fetch.bind(window);
        window.fetch = function (resource, init) {
            try {
                var url = typeof resource === 'string' ? resource
                    : (resource && typeof resource.url === 'string' ? resource.url : '');
                if(url.indexOf(INSIGHTS_HOST) !== -1) {
                    var body = init && init.body;
                    if(typeof body === 'string') {
                        var filtered = filterInsightsBody(body);
                        if(filtered === null) {
                            console.log('[Algolia] Insights filter: all events blocked, suppressing fetch');
                            return Promise.resolve(new Response('{}', { status: 200 }));
                        }
                        init = Object.assign({}, init, { body: filtered });
                    }
                }
            } catch (e) {
                console.warn('[Algolia] Insights filter: error in fetch patch, passing through:', e);
            }
            return _originalFetch(resource, init);
        };
    }

    // --- Patch navigator.sendBeacon ---
    if(typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        var _originalSendBeacon = navigator.sendBeacon.bind(navigator);
        navigator.sendBeacon = function (url, data) {
            try {
                if(typeof url === 'string' && url.indexOf(INSIGHTS_HOST) !== -1 && typeof data === 'string') {
                    var filtered = filterInsightsBody(data);
                    if(filtered === null) {
                        console.log('[Algolia] Insights filter: all events blocked, suppressing sendBeacon');
                        return true;
                    }
                    data = filtered;
                }
            } catch (e) {
                console.warn('[Algolia] Insights filter: error in sendBeacon patch, passing through:', e);
            }
            return _originalSendBeacon(url, data);
        };
    }

    // --- Patch XMLHttpRequest (fallback for older browsers / polyfills) ---
    if(typeof window.XMLHttpRequest === 'function') {
        var _XHROpen = XMLHttpRequest.prototype.open;
        var _XHRSend = XMLHttpRequest.prototype.send;

        XMLHttpRequest.prototype.open = function (method, url) {
            try {
                this._algoliaInsightsUrl = (typeof url === 'string' && url.indexOf(INSIGHTS_HOST) !== -1) ? url : null;
            } catch (e) {
                console.warn('[Algolia] Insights filter: error in XHR open patch, passing through:', e);
            }
            return _XHROpen.apply(this, arguments);
        };

        XMLHttpRequest.prototype.send = function (body) {
            try {
                if(this._algoliaInsightsUrl && typeof body === 'string') {
                    var filtered = filterInsightsBody(body);
                    if(filtered === null) {
                        console.log('[Algolia] Insights filter: all events blocked, suppressing XHR');
                        return;
                    }
                    body = filtered;
                }
            } catch (e) {
                console.warn('[Algolia] Insights filter: error in XHR send patch, passing through:', e);
            }
            return _XHRSend.call(this, body);
        };
    }
}());
